import { ReactNode, useState } from 'react'
import { CloseIcon } from 'shared/icons'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import Grid, { GridProps } from '@mui/material/Grid'
import { Stack, Paper, Box, Typography, IconButton } from '@mui/material'
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material'

type EdittableDataListRecordProps<T> = {
  data: T
  displayName: string
  onEditIt: () => void
  onRemoveIt: () => void
  children?: ReactNode
} & GridProps

export const EdittableDataListRecord = <T extends { id?: number }>({
  data,
  displayName,
  children,
  onEditIt,
  onRemoveIt,
  ...others
}: EdittableDataListRecordProps<T>) => {
  const {
    setNodeRef,
    isDragging,
    transform,
    setActivatorNodeRef,
    attributes,
    listeners,
  } = useSortable({ id: data.id ?? -1 })

  const [hover, setHover] = useState<boolean>(false)

  return (
    <Grid ref={setNodeRef} {...others}>
      <Paper
        variant="outlined"
        style={isDragging ? {
          boxShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          transform: CSS.Transform.toString(transform),
        } : {
          transform: CSS.Transform.toString(transform),
        }}
      >
        <Stack alignItems="center" direction="row" spacing={0.5} paddingX={0.5}>
          <Box
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <DragIndicatorIcon style={{ display: 'block' }} />
          </Box>

          <Stack
            flexGrow={1}
            alignItems="center"
            direction="row"
            spacing={0.5}
            overflow="hidden"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <Stack
              flexGrow={1}
              alignItems="center"
              direction="row"
              paddingY={0.5}
              onClick={onEditIt}
              sx={{ cursor: 'pointer' }}
              overflow="hidden"
            >
              <Typography
                flexGrow={1}
                overflow="hidden"
                variant="body1"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {displayName}
              </Typography>

              {children}
            </Stack>

            {hover && (
              <IconButton
                aria-label="delete"
                size="small"
                onClick={onRemoveIt}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Grid>
  )
}

type StaticDataListRecordProps = {
  displayName: string
  children?: ReactNode
} & GridProps

export const StaticDataListRecord = ({
  displayName,
  children,
  ...others
}: StaticDataListRecordProps) => (
  <Grid {...others}>
    <Paper variant="outlined">
      <Stack alignItems="center" direction="row" spacing={0.5} paddingX={1} paddingY={0.5}>
        <Typography
          flexGrow={1}
          overflow="hidden"
          variant="body1"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {displayName}
        </Typography>

        {children}
      </Stack>
    </Paper>
  </Grid>
)
